/**
 * useLocationSelect
 *
 * 统一处理从 location-select 页面返回的位置数据，
 * 供 cars / hotels / flights 等列表页复用。
 *
 * 返回值：
 *   - currentCity: 城市名称
 *   - currentTownship: 地址/街道（地图选点时为乡镇，城市搜索时等于城市名）
 *   - coords: { latitude, longitude }
 *   - locationDisplay: 展示文本（根据 source 区分：city_search 只显示城市名）
 *   - source: 来源标识 "city_search" | "map_select" | "recent" | "location"
 */

import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getLocationWithCache, getCachedLocation } from "@/utils/location";

export type LocationSource = "city_search" | "map_select" | "recent" | "location";

interface UseLocationSelectOptions {
  /** 兜底默认城市 */
  defaultCity?: string;
}

interface UseLocationSelectReturn {
  /** 城市名称 */
  currentCity: string;
  /** 街道/乡镇名称 */
  currentTownship: string;
  /** 经纬度坐标 */
  coords: { latitude: string; longitude: string };
  /**
   * 展示文本：
   * - city_search → 仅显示城市名（如 "成都市"）
   * - 其他来源 → 城市 · 街道（如 "成都市 · 中和街道"）
   */
  locationDisplay: string;
  /** 来源类型 */
  source: LocationSource | "";
  /** 是否正在加载中 */
  loading: boolean;
}

export function useLocationSelect(
  options: UseLocationSelectOptions = {}
): UseLocationSelectReturn {
  const { defaultCity = "成都" } = options;

  const routerLocation = useLocation();
  const stateSelected = (routerLocation.state as any) || {};

  const [currentCity, setCurrentCity] = useState<string>("定位中...");
  const [currentTownship, setCurrentTownship] = useState<string>("");
  const [coords, setCoords] = useState<{ latitude: string; longitude: string }>({
    latitude: "",
    longitude: "",
  });
  const [loading, setLoading] = useState(true);

  const source: LocationSource | "" = (stateSelected.source as LocationSource) || "";

  // 根据来源决定展示方式
  const locationDisplay =
    source === "city_search"
      ? currentCity
      : currentTownship
        ? `${currentCity} · ${currentTownship}`
        : currentCity;

  // 加载位置信息
  useEffect(() => {
    const loadLocation = async () => {
      try {
        // 优先：从位置选择页返回的 state 中读取
        if (stateSelected.selectedCity) {
          setCurrentCity(stateSelected.selectedCity);
          setCurrentTownship(stateSelected.selectedTownship || "");
          setCoords({
            latitude: String(stateSelected.selectedLat || ""),
            longitude: String(stateSelected.selectedLng || ""),
          });
          setLoading(false);
          return;
        }

        // 兜底：从缓存或定位获取
        let city = "";
        let township = "";
        let lat = "";
        let lng = "";

        const cached = getCachedLocation();
        if (cached) {
          city = cached.city || cached.district || defaultCity;
          township = cached.township || "";
          lat = String(cached.latitude || "");
          lng = String(cached.longitude || "");
        } else {
          const loc = await getLocationWithCache({ withAddress: true });
          city = loc.city || loc.district || defaultCity;
          township = loc.township || "";
          lat = String(loc.latitude || "");
          lng = String(loc.longitude || "");
        }

        setCurrentCity(city);
        setCurrentTownship(township);
        setCoords({ latitude: lat, longitude: lng });
      } catch {
        setCurrentCity(defaultCity);
      } finally {
        setLoading(false);
      }
    };
    loadLocation();
    // 依赖 stateSelected 关键字段，确保从城市选择页返回时能正确读取经纬度
  }, [defaultCity, stateSelected.selectedCity, stateSelected.selectedLat, stateSelected.selectedLng, stateSelected.selectedTownship]);

  return { currentCity, currentTownship, coords, locationDisplay, source, loading };
}
